import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Building2, MapPin, Calendar, ArrowLeft, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubcontractRequestsPage() {
  const { requestId } = useParams();
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (requestId) {
      fetchSingleRequest();
    } else {
      fetchRequests();
    }
  }, [requestId]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/subcontract-requests?active_only=true`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching subcontract requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleRequest = async () => {
    try {
      const response = await axios.get(`${API}/subcontract-requests/${requestId}`);
      setSelectedRequest(response.data);
    } catch (error) {
      console.error('Error fetching subcontract request:', error);
    } finally {
      setLoading(false);
    }
  };

  // Single request detail view
  if (requestId && selectedRequest) {
    return (
      <div data-testid="subcontract-detail">
        <Navbar />
        <main>
          <section className="hero-dark py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link to="/richieste-subappalto" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6">
                <ArrowLeft size={20} />
                Torna alle richieste
              </Link>
              <div className="accent-line mb-6"></div>
              <div className="flex items-start gap-4 mb-4">
                <span className="bg-[#E94560] text-white px-4 py-2 font-bold">{selectedRequest.specialization}</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
                {selectedRequest.title}
              </h1>
              <p className="text-xl text-gray-300 mb-4">{selectedRequest.project_name}</p>
              <div className="flex flex-wrap gap-6 text-gray-300">
                <span className="flex items-center gap-2"><MapPin size={18} /> {selectedRequest.location}</span>
                <span className="flex items-center gap-2"><Calendar size={18} /> Inizio: {selectedRequest.start_date}</span>
                <span>Durata: {selectedRequest.duration}</span>
                {selectedRequest.budget_range && <span className="text-[#27AE60] font-bold">{selectedRequest.budget_range}</span>}
              </div>
            </div>
          </section>

          <section className="py-16 bg-[#F5F5F7]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-8">
                  <div className="bg-white p-8 border-2 border-gray-200">
                    <h2 className="text-xl font-bold text-[#1A1A2E] mb-4">Descrizione del progetto</h2>
                    <p className="text-[#555555] whitespace-pre-line">{selectedRequest.description}</p>
                  </div>
                  <div className="bg-white p-8 border-2 border-gray-200">
                    <h2 className="text-xl font-bold text-[#1A1A2E] mb-4">Requisiti richiesti</h2>
                    <p className="text-[#555555] whitespace-pre-line">{selectedRequest.requirements}</p>
                  </div>
                </div>
                <div>
                  <div className="bg-white p-8 border-2 border-[#E94560] sticky top-24">
                    <h3 className="text-lg font-bold text-[#1A1A2E] mb-4">Vuoi partecipare?</h3>
                    <p className="text-[#555555] mb-6">Candidati con la tua impresa per questo progetto.</p>
                    <Link to="/lavora-con-noi/imprese" className="btn-primary w-full text-center flex items-center justify-center gap-2">
                      <Send size={18} /> Candidati
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // List view
  return (
    <div data-testid="subcontract-page">
      <Navbar />
      <main>
        <section className="hero-dark py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="accent-line mb-6"></div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Richieste di Subappalto
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl">
              Opportunità per imprese subappaltatrici nel settore EPC e industriale.
            </p>
          </div>
        </section>

        <section className="py-16 bg-[#F5F5F7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-12">Caricamento...</div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-[#888888]" />
                <p className="text-[#555555]">Nessuna richiesta di subappalto al momento.</p>
                <p className="text-[#888888] mt-2">Torna a trovarci presto!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {requests.map((req) => (
                  <Link
                    key={req.id}
                    to={`/richieste-subappalto/${req.id}`}
                    className="bg-white border-2 border-gray-200 hover:border-[#E94560] p-6 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#E94560] flex items-center justify-center">
                        <Building2 className="text-white" size={24} />
                      </div>
                      <span className="text-xs font-bold bg-[#27AE60] text-white px-3 py-1">
                        {req.specialization}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#1A1A2E] mb-2 group-hover:text-[#E94560] transition-colors">
                      {req.title}
                    </h3>
                    <p className="text-sm text-[#555555] mb-3">{req.project_name}</p>
                    <div className="flex flex-wrap gap-3 text-sm text-[#888888]">
                      <span className="flex items-center gap-1"><MapPin size={14} />{req.location}</span>
                      <span className="flex items-center gap-1"><Calendar size={14} />{req.start_date}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#888888]">Durata: {req.duration}</p>
                    {req.budget_range && <p className="mt-2 text-sm font-bold text-[#27AE60]">{req.budget_range}</p>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
